/**
 * Overflow warning on the Merge jar creatures: an inked orange rim ring that pulses on each body
 * poking into the warning zone, drifting to red on the bodies at the alert line once the jar is
 * in alert, plus a small "!" badge bouncing on each. Baked marks (render/toon/jar/jar-marks.js)
 * drawn as sprites or images: no additive glows, blur filters or shadowBlur.
 */
import * as C from "../config/constants.js";
import { getBodyOverflowZoneTier } from "../flask/system/overflow-state.js";
import { getJarMark, ringFrameSize } from "./toon/jar/jar-marks.js";
import { displayBoost } from "./toon/jar/jar-bodies.js";

var WARNING_RGB = { r: 255, g: 116, b: 16 };
var ALERT_RGB = { r: 255, g: 20, b: 16 };

function lerpColor(a, b, t) {
  var k = Math.max(0, Math.min(1, t));
  return {
    r: Math.round(a.r + (b.r - a.r) * k),
    g: Math.round(a.g + (b.g - a.g) * k),
    b: Math.round(a.b + (b.b - a.b) * k),
  };
}

function cfgMs(key, fallback) {
  var v = C[key] != null ? Number(C[key]) : fallback;
  return isFinite(v) && v >= 0 ? v : fallback;
}

function rgba(c, a) {
  return "rgba(" + c.r + "," + c.g + "," + c.b + "," + Math.max(0, Math.min(1, a)) + ")";
}

/**
 * Smooth 0–1 pulse.
 * @param {number} now
 * @param {number} periodMs
 * @returns {number}
 */
export function getOverflowOutlinePulse(now, periodMs) {
  var t = now != null ? now : (typeof performance !== "undefined" ? performance.now() : Date.now());
  var period = periodMs != null && isFinite(periodMs) && periodMs >= 200 ? periodMs : 1400;
  var phase = (t % period) / period;
  return 0.5 + 0.5 * Math.sin(phase * Math.PI * 2);
}

/**
 * Body draw ellipse in flask-panel space, mirroring FlaskRenderer: the shrink tween's radius
 * and the jar's display boost (bodies are drawn a little past their physics circle).
 * (Merging bodies no longer swell when drawn, so the merge tween's scale is not applied.)
 */
function bodyDrawMetrics(meta, system) {
  var body = meta.body;
  var tier = meta.tier != null ? meta.tier : 0;
  var byTier = system.bodyRadiusByTier || [];
  var radius = byTier[tier] != null ? byTier[tier] : 10;
  var rx = meta.radiusX != null ? meta.radiusX : radius;
  var ry = meta.radiusY != null ? meta.radiusY : radius;
  var shrink = system.shrinkMotionDraw && system.shrinkMotionDraw[body.id];
  if (shrink) {
    rx = shrink.drawRadiusX != null ? shrink.drawRadiusX : rx;
    ry = shrink.drawRadiusY != null ? shrink.drawRadiusY : ry;
  }
  var boost = displayBoost(tier);
  return {
    x: body.position.x,
    y: body.position.y,
    rx: Math.max(3, rx * boost),
    ry: Math.max(3, ry * boost),
    angle: body.angle || 0,
  };
}

function ellipsePath(ctx, x, y, rx, ry, angle) {
  ctx.beginPath();
  if (typeof ctx.ellipse === "function") {
    ctx.ellipse(x, y, rx, ry, angle || 0, 0, Math.PI * 2);
  } else {
    ctx.arc(x, y, Math.max(rx, ry), 0, Math.PI * 2);
  }
}

function easeOutBack(k) {
  var c1 = 1.9;
  var t = Math.max(0, Math.min(1, k)) - 1;
  return 1 + (c1 + 1) * t * t * t + c1 * t * t;
}

/**
 * Where and how big one in-zone body's marks are this frame (shared by Pixi and Canvas 2D):
 * the ring swells with the pulse and pops in with the fade; the badge hops on the body's upper
 * right, squashing as it lands (faster in alert).
 * @param {{ x: number, y: number, rx: number, ry: number }} m body ellipse (panel px)
 * @param {number} pulse 0–1
 * @param {number} fade 0–1 (the per-body fade in/out)
 * @param {number} alertMix 0 orange … 1 red
 * @param {number} now ms
 */
export function overflowMarkLayout(m, pulse, fade, alertMix, now) {
  var f = Math.max(0, Math.min(1, fade));
  var rMax = Math.max(m.rx, m.ry);
  var pop = easeOutBack(f);
  // Band centred just outside the creature's ink.
  var ringR = rMax * (1.05 + 0.06 * pulse) * (0.82 + 0.18 * pop);
  var size = Math.max(17, Math.min(30, rMax * 0.62)) * pop;
  var hz = 1.5 + 1.1 * alertMix;
  var hop = Math.abs(Math.sin(((now || 0) / 1000) * Math.PI * hz));
  var land = Math.pow(1 - hop, 4);
  return {
    ringR: ringR,
    ringAlpha: f * (0.78 + 0.22 * pulse),
    badgeX: m.x + rMax * 0.66,
    badgeY: m.y - rMax * 0.74 - hop * size * 0.32,
    badgeSize: size,
    badgeSquash: 1 - 0.2 * land,
    badgeAlpha: Math.min(1, f * 1.25),
    alertMix: Math.max(0, Math.min(1, alertMix)),
  };
}

function publishOverflowMarks(playfield, origin, id, m, color, mark) {
  origin = origin || { x: 0, y: 0 };
  var x = origin.x + m.x;
  var y = origin.y + m.y;
  var rMax = Math.max(m.rx, m.ry);
  var kx = m.rx / rMax;
  var ky = m.ry / rMax;
  var warnRing = getJarMark("ring", "warn", rMax);
  var alertRing = getJarMark("ring", "alert", rMax);
  if (!warnRing || !alertRing) {
    // No 2D canvas for the baked marks: an ink ring under a coloured one.
    playfield.effect({
      key: "ov-ink-" + id, kind: "ellipse", x: x, y: y,
      radiusX: mark.ringR * kx, radiusY: mark.ringR * ky, rotation: m.angle || 0,
      rgb: [23, 42, 58], alpha: mark.ringAlpha, stroke: Math.max(3, mark.ringR * 0.14), zIndex: 40,
    });
    playfield.effect({
      key: "ov-rim-" + id, kind: "ellipse", x: x, y: y,
      radiusX: mark.ringR * kx, radiusY: mark.ringR * ky, rotation: m.angle || 0,
      rgb: [color.r, color.g, color.b], alpha: mark.ringAlpha, stroke: Math.max(1.5, mark.ringR * 0.08), zIndex: 41,
    });
    return;
  }
  var fw = ringFrameSize(mark.ringR);
  if (mark.alertMix < 0.98) {
    playfield.effect({
      key: "ov-ring-" + id, kind: "texture", image: warnRing, x: x, y: y,
      width: fw * kx, height: fw * ky, rotation: m.angle || 0, alpha: mark.ringAlpha, zIndex: 40,
    });
  }
  if (mark.alertMix > 0.02) {
    // Red over orange with the mix as its alpha: the shared ink stays solid through the blend.
    playfield.effect({
      key: "ov-ring-a-" + id, kind: "texture", image: alertRing, x: x, y: y,
      width: fw * kx, height: fw * ky, rotation: m.angle || 0,
      alpha: mark.ringAlpha * (mark.alertMix < 0.98 ? mark.alertMix : 1), zIndex: 41,
    });
  }
  var badge = getJarMark("badge", mark.alertMix > 0.5 ? "alert" : "warn");
  if (badge && mark.badgeSize > 1) {
    // Anchored at its bottom so the landing squash sits on the hop's floor.
    playfield.effect({
      key: "ov-badge-" + id, kind: "texture", image: badge,
      x: origin.x + mark.badgeX, y: origin.y + mark.badgeY + mark.badgeSize / 2,
      width: mark.badgeSize / mark.badgeSquash, height: mark.badgeSize * mark.badgeSquash,
      anchorX: 0.5, anchorY: 1, alpha: mark.badgeAlpha, zIndex: 43,
    });
  }
}

function drawOverflowMarks(ctx, m, color, mark) {
  var rMax = Math.max(m.rx, m.ry);
  var warnRing = getJarMark("ring", "warn", rMax);
  var alertRing = getJarMark("ring", "alert", rMax);
  var kx = m.rx / rMax;
  var ky = m.ry / rMax;
  ctx.save();
  if (!warnRing || !alertRing || typeof ctx.drawImage !== "function") {
    // Vector fallback (no baked marks): an ink ring, then the coloured band on it.
    ctx.globalAlpha = Math.min(1, mark.ringAlpha);
    ctx.lineWidth = Math.max(3, mark.ringR * 0.14);
    ctx.strokeStyle = "rgba(23,42,58,1)";
    ellipsePath(ctx, m.x, m.y, mark.ringR * kx, mark.ringR * ky, m.angle);
    ctx.stroke();
    ctx.lineWidth = Math.max(1.5, mark.ringR * 0.08);
    ctx.strokeStyle = rgba(color, 1);
    ellipsePath(ctx, m.x, m.y, mark.ringR * kx, mark.ringR * ky, m.angle);
    ctx.stroke();
    ctx.restore();
    return;
  }
  var fw = ringFrameSize(mark.ringR);
  ctx.translate(m.x, m.y);
  if (m.angle) ctx.rotate(m.angle);
  if (mark.alertMix < 0.98) {
    ctx.globalAlpha = mark.ringAlpha;
    ctx.drawImage(warnRing, -fw * kx / 2, -fw * ky / 2, fw * kx, fw * ky);
  }
  if (mark.alertMix > 0.02) {
    ctx.globalAlpha = mark.ringAlpha * (mark.alertMix < 0.98 ? mark.alertMix : 1);
    ctx.drawImage(alertRing, -fw * kx / 2, -fw * ky / 2, fw * kx, fw * ky);
  }
  ctx.restore();
  var badge = getJarMark("badge", mark.alertMix > 0.5 ? "alert" : "warn");
  if (badge && mark.badgeSize > 1) {
    var bw = mark.badgeSize / mark.badgeSquash;
    var bh = mark.badgeSize * mark.badgeSquash;
    ctx.save();
    ctx.globalAlpha = mark.badgeAlpha;
    ctx.drawImage(badge, mark.badgeX - bw / 2, mark.badgeY + mark.badgeSize / 2 - bh, bw, bh);
    ctx.restore();
  }
}

function approach(current, target, step) {
  if (current < target) return Math.min(target, current + step);
  if (current > target) return Math.max(target, current - step);
  return target;
}

/**
 * Ring each in-zone body (orange while `warning`, red on the bodies at the alert line while
 * `alert`) and badge it, fading in/out over `FLASK_OVERFLOW_GLOW_FADE_MS` so it never pops on
 * or off. Draw after FlaskSystem.draw, in the same (flask-panel) transform.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   system: import("../flask/flask-system.js").FlaskSystem|null|undefined,
 *   state: import("../flask/system/overflow-state.js").OverflowState|null|undefined,
 *   now?: number,
 *   playfield?: { effect?: Function },
 *   worldOrigin?: { x: number, y: number },
 * }} opts
 */
export function drawOverflowOutlines(ctx, opts) {
  if (!opts || !opts.state) return;
  if (!ctx && !(opts.playfield && typeof opts.playfield.effect === "function")) return;
  var state = opts.state;
  var system = opts.system;

  var glow = state._glow;
  if (!glow) { glow = state._glow = { byId: {}, lastNow: null }; }

  var active = state.state === "warning" || state.state === "alert";
  var bodies = system && system.bodies ? system.bodies : [];

  // Nothing to draw and nothing fading — stay idle (and reset the clock so a later
  // appearance starts a fresh fade-in rather than catching up a stale delta).
  var hasResidual = false;
  for (var k in glow.byId) { hasResidual = true; break; }
  if (!active && !hasResidual) { glow.lastNow = null; return; }

  var now = opts.now != null ? opts.now : (typeof performance !== "undefined" ? performance.now() : Date.now());
  var dt = glow.lastNow == null ? 0 : Math.max(0, Math.min(100, now - glow.lastNow));
  glow.lastNow = now;
  var fadeMs = cfgMs("FLASK_OVERFLOW_GLOW_FADE_MS", 300);
  var step = fadeMs > 0 ? dt / fadeMs : 1;
  var colorMs = cfgMs("FLASK_OVERFLOW_GLOW_COLOR_MS", 300);
  var colorStep = colorMs > 0 ? dt / colorMs : 1;

  var warnPulse = getOverflowOutlinePulse(now, cfgMs("FLASK_OVERFLOW_WARN_BLINK_MS", 1400) || 1400);
  var alertPulse = getOverflowOutlinePulse(now, cfgMs("FLASK_OVERFLOW_OUTLINE_ALERT_PULSE_MS", 760) || 760);
  var stateIsAlert = state.state === "alert";

  var seen = {};
  for (var i = 0; i < bodies.length; i++) {
    var meta = bodies[i];
    if (!meta || !meta.body) continue;
    var id = meta.body.id;
    seen[id] = true;

    // In warning state everything reads orange; only once the flask is in alert do the
    // bodies at the alert line drift toward red. Keeps colour aligned with loss pressure.
    var bodyTier = active ? getBodyOverflowZoneTier(meta, system) : null;
    var tier = bodyTier ? (stateIsAlert ? bodyTier : "warning") : null;
    var target = tier ? 1 : 0;

    var entry = glow.byId[id] || (glow.byId[id] = { a: 0, c: 0 });
    entry.a = approach(entry.a, target, step);
    // Only steer the orange↔red mix while the body is in a zone; keep the last mix
    // through the fade-out so the colour doesn't jump as it disappears.
    if (tier) entry.c = approach(entry.c, tier === "alert" ? 1 : 0, colorStep);

    if (entry.a <= 0.003 && target === 0) { delete glow.byId[id]; continue; }
    if (entry.a <= 0.02) continue;

    var color = lerpColor(WARNING_RGB, ALERT_RGB, entry.c);
    var pulse = warnPulse + (alertPulse - warnPulse) * entry.c;
    var metrics = bodyDrawMetrics(meta, system);
    var mark = overflowMarkLayout(metrics, pulse, entry.a, entry.c, now);
    if (opts.playfield && typeof opts.playfield.effect === "function") {
      publishOverflowMarks(opts.playfield, opts.worldOrigin, id, metrics, color, mark);
    } else {
      drawOverflowMarks(ctx, metrics, color, mark);
    }
  }

  // Bodies that vanished (removed/bombed) can't be drawn — drop their fade entries.
  for (var gid in glow.byId) {
    if (!seen[gid]) delete glow.byId[gid];
  }
}
